import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, MessageCircle, Send, Clock, Building2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import PageMeta from '../PageMeta';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactUs = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast({ title: 'Please fill all fields', variant: 'destructive' });
      return;
    }
    if (form.phone.replace(/\D/g, '').length < 10) {
      toast({ title: 'Enter a valid 10-digit phone', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast({ title: 'Message sent', description: "We'll get back to you within 1 business day." });
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (e) {
      toast({ title: 'Failed to send', description: e.response?.data?.detail || 'Try again or email us directly.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <PageMeta
        title="Contact Us"
        description="Get in touch with RentKollect (Ramde Business Solutions LLP). Support, sales, partnership and refund queries — email, WhatsApp or visit our Gurgaon office."
        canonical="https://rentkollect.com/contact"
      />

      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-600 via-green-600 to-teal-600 text-white py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <a href="/" className="text-emerald-100 hover:text-white text-sm font-semibold">← Back to RentKollect</a>
          <h1 className="text-4xl md:text-5xl font-bold mt-3">Contact Us</h1>
          <p className="text-emerald-50 mt-3 text-lg max-w-2xl">
            Questions, feedback, a refund query, or just want to say hi? Reach us on any of the channels below — we answer every message.
          </p>
          <p className="text-emerald-100/90 mt-2 text-[13px]">
            RentKollect is a brand of <strong>Ramde Business Solutions LLP</strong> · LLPIN ACR-9891 · GST 06ABMFR3856J1ZO
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-5 gap-8">
        {/* Left: contact details */}
        <div className="md:col-span-2 space-y-4">
          <ContactRow
            Icon={Mail}
            title="Email"
            value="hello@rentkollect.com"
            href="mailto:hello@rentkollect.com"
            testid="contact-email"
          />
          <ContactRow
            Icon={MessageCircle}
            title="WhatsApp"
            value="+91 98219 22296"
            href="https://wa.me/919821922296"
            testid="contact-whatsapp"
            sub="Fastest channel — typically under 2 hours on business days."
          />
          <ContactRow
            Icon={Phone}
            title="Phone"
            value="+91 98219 22296"
            href="tel:+919821922296"
            testid="contact-phone"
          />
          <ContactRow
            Icon={MapPin}
            title="Registered office"
            value={<>
              H.No 59, Neelkanth Enclave,<br/>
              near Ekta Hospital, Badshahpur,<br/>
              Gurgaon, Haryana 122101, India
            </>}
            testid="contact-address"
          />
          <ContactRow
            Icon={Building2}
            title="Legal entity"
            value={<>
              Ramde Business Solutions LLP<br/>
              <span className="text-xs text-gray-500">LLPIN ACR-9891 · GSTIN 06ABMFR3856J1ZO</span>
            </>}
            testid="contact-entity"
          />
          <ContactRow
            Icon={Clock}
            title="Business hours"
            value="Mon – Sat · 10:00 AM – 7:00 PM IST"
            testid="contact-hours"
          />
        </div>

        {/* Right: form */}
        <Card className="md:col-span-3 p-6 md:p-8 shadow-sm">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Send us a message</h2>
          <p className="text-sm text-gray-500 mb-6">
            We reply within 1 business day. For refund / cancellation queries, please also include
            your registered mobile number and transaction ID.
          </p>

          {sent && (
            <div className="mb-5 p-3 rounded-lg border border-emerald-200 bg-emerald-50 text-sm text-emerald-800" data-testid="contact-success">
              ✅ Message sent. We'll get back to you within 1 business day.
            </div>
          )}

          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="c-name">Your name *</Label>
                <Input
                  id="c-name"
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={e => update('name', e.target.value)}
                  placeholder="Ashish Kumar"
                  required
                />
              </div>
              <div>
                <Label htmlFor="c-phone">Phone *</Label>
                <Input
                  id="c-phone"
                  data-testid="contact-phone-input"
                  type="tel"
                  value={form.phone}
                  onChange={e => update('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="9876543210"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="c-email">Email *</Label>
              <Input
                id="c-email"
                data-testid="contact-email-input"
                type="email"
                value={form.email}
                onChange={e => update('email', e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
            <div>
              <Label htmlFor="c-message">Message *</Label>
              <textarea
                id="c-message"
                data-testid="contact-message-input"
                value={form.message}
                onChange={e => update('message', e.target.value)}
                rows={6}
                placeholder="Tell us how we can help…"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 text-sm"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="contact-submit"
            >
              {saving ? 'Sending…' : (<><Send size={16} className="mr-2" /> Send message</>)}
            </Button>
            <p className="text-xs text-gray-500 text-center">
              By submitting, you agree to our <a href="/privacy" className="text-emerald-600 hover:underline">Privacy Policy</a>.
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
};

const ContactRow = ({ Icon, title, value, href, sub, testid }) => (
  <div data-testid={testid} className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 hover:border-emerald-300 transition-colors">
    <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
      <Icon size={18} className="text-emerald-600" />
    </div>
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{title}</p>
      {href ? (
        <a href={href} className="block text-gray-900 font-medium hover:text-emerald-700 text-sm md:text-base" target={href.startsWith('http') ? '_blank' : undefined} rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}>
          {value}
        </a>
      ) : (
        <div className="text-gray-900 font-medium text-sm md:text-base">{value}</div>
      )}
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  </div>
);

export default ContactUs;
